import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  findScheduleWarnings,
  warningHeadline,
  type AvailabilitySlot,
  type ClashCandidate,
  type ClashJob,
  type ClashMember,
  type OpeningHours,
  type ScheduleOverride,
  type ScheduleWarning,
} from '@/lib/schedule-clash';

interface OrgScheduleRules {
  members: ClashMember[];
  openingHours: OpeningHours[];
  bufferMinutes: number;
}

const RULES_KEY = 'schedule-clash-rules';

async function loadOrgRules(orgId: string): Promise<OrgScheduleRules> {
  const [membersRes, hoursRes, companyRes] = await Promise.all([
    supabase.from('team_members').select('id, name').eq('organization_id', orgId),
    supabase.from('business_hours').select('day_of_week, is_open, open_time, close_time').eq('organization_id', orgId),
    supabase
      .from('company_info')
      .select('buffer_minutes')
      .eq('organization_id', orgId)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle(),
  ]);
  if (membersRes.error) throw membersRes.error;
  if (hoursRes.error) throw hoursRes.error;
  return {
    members: (membersRes.data ?? []) as ClashMember[],
    openingHours: (hoursRes.data ?? []) as OpeningHours[],
    // Only respect a buffer the business has actually set (same as online booking).
    bufferMinutes: companyRes.error ? 0 : companyRes.data?.buffer_minutes ?? 0,
  };
}

async function loadDay(orgId: string, candidate: ClashCandidate) {
  const members = Array.from(new Set(candidate.memberIds.filter(Boolean)));
  const [jobsRes, availabilityRes, overridesRes] = await Promise.all([
    members.length
      ? supabase
          .from('jobs')
          .select('id, title, scheduled_date, start_time, end_time, status, address, team_member_id, job_assignments(team_member_id)')
          .eq('organization_id', orgId)
          .eq('scheduled_date', candidate.date)
          .neq('status', 'cancelled')
      : Promise.resolve({ data: [], error: null }),
    members.length
      ? supabase
          .from('team_availability')
          .select('team_member_id, day_of_week, start_time, end_time, is_available')
          .in('team_member_id', members)
      : Promise.resolve({ data: [], error: null }),
    members.length
      ? supabase
          .from('team_schedule_overrides')
          .select('team_member_id, override_date, start_time, end_time, is_blocked, reason')
          .in('team_member_id', members)
          .eq('override_date', candidate.date)
      : Promise.resolve({ data: [], error: null }),
  ]);
  if (jobsRes.error) throw jobsRes.error;
  return {
    jobs: (jobsRes.data ?? []) as ClashJob[],
    // Availability and time off are extra context: if they can't be read
    // (permissions, older schema) we still check for double bookings.
    availability: (availabilityRes.error ? [] : availabilityRes.data ?? []) as AvailabilitySlot[],
    overrides: (overridesRes.error ? [] : overridesRes.data ?? []) as ScheduleOverride[],
  };
}

export interface ScheduleClashPrompt {
  open: boolean;
  headline: string;
  warnings: ScheduleWarning[];
  /** Repeat-clean context: later visits that clash are held back server-side. */
  repeating: boolean;
  onBookAnyway: () => void;
  onPickAnother: () => void;
}

export interface ConfirmScheduleOptions {
  /** Jobs already on screen — used if the live check can't load, so we never stay silent. */
  fallbackJobs?: ClashJob[];
  fallbackMembers?: ClashMember[];
  repeating?: boolean;
}

/**
 * One guard for every place an owner or manager books, moves or assigns a job.
 * `confirmSchedule(candidate)` loads that day's jobs (not just the week on
 * screen), the cleaners' availability and time off, the opening hours and the
 * buffer between jobs. If anything clashes it opens the shared prompt and
 * resolves true for "Book anyway", false for "Pick another time".
 * Render <ScheduleClashDialog {...clashPrompt} /> once in the screen.
 */
export function useScheduleClashCheck(orgId: string | null | undefined) {
  const queryClient = useQueryClient();
  const resolver = useRef<((ok: boolean) => void) | null>(null);
  const [state, setState] = useState<{ open: boolean; warnings: ScheduleWarning[]; repeating: boolean }>({
    open: false,
    warnings: [],
    repeating: false,
  });

  const findWarnings = useCallback(
    async (candidate: ClashCandidate, options: ConfirmScheduleOptions = {}): Promise<ScheduleWarning[]> => {
      if (!orgId) return [];
      try {
        const [rules, day] = await Promise.all([
          queryClient.fetchQuery({ queryKey: [RULES_KEY, orgId], queryFn: () => loadOrgRules(orgId), staleTime: 60_000 }),
          loadDay(orgId, candidate),
        ]);
        return findScheduleWarnings(candidate, { ...rules, ...day });
      } catch (error) {
        console.error('Could not load the calendar for a clash check; using what is on screen', error);
        return findScheduleWarnings(candidate, {
          jobs: options.fallbackJobs ?? [],
          members: options.fallbackMembers ?? [],
        });
      }
    },
    [orgId, queryClient],
  );

  const settle = useCallback((ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const confirmSchedule = useCallback(
    async (candidate: ClashCandidate, options: ConfirmScheduleOptions = {}): Promise<boolean> => {
      const warnings = await findWarnings(candidate, options);
      if (warnings.length === 0) return true;
      // A prompt already waiting is treated as "pick another time".
      resolver.current?.(false);
      return new Promise<boolean>((resolve) => {
        resolver.current = resolve;
        setState({ open: true, warnings, repeating: Boolean(options.repeating) });
      });
    },
    [findWarnings],
  );

  const clashPrompt: ScheduleClashPrompt = {
    open: state.open,
    headline: warningHeadline(state.warnings),
    warnings: state.warnings,
    repeating: state.repeating,
    onBookAnyway: () => settle(true),
    onPickAnother: () => settle(false),
  };

  return { confirmSchedule, findWarnings, clashPrompt };
}

